import { Element, Editor, Path, Range, Transforms } from 'slate'
import { ReactEditor } from 'slate-react'
import { jsx } from 'slate-hyperscript'
import ReactDOMServer from 'react-dom/server'
import DOMPurify from 'dompurify'

import {
  NODE_TYPE,
  LIST_TYPES,
  ELEMENT_TYPES,
  MARK_TYPES,
  SHORT_CODES_LIST,
} from './text-editor.constants'

const createLinkNode = (href, text) => ({
  type: ELEMENT_TYPES.LINK,
  href,
  children: [{ text }],
})

export const createParagraphNode = (children = [{ text: '' }]) => ({
  type: ELEMENT_TYPES.PARAGRAPH,
  children,
})

export const isBlockActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => n.type === format,
  })
  return !!match
}

export const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor)
  return !!marks?.[format]
}

export const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format)
  const isList = LIST_TYPES.includes(format)
  const paragraph = isActive && ELEMENT_TYPES.PARAGRAPH
  const listItem = isList && ELEMENT_TYPES.LIST_ITEM

  Transforms.unwrapNodes(editor, {
    match: (n) => LIST_TYPES.includes(n.type),
    split: true,
  })

  Transforms.setNodes(editor, {
    type: paragraph || listItem || format,
  })

  if (!isActive && isList) {
    const block = { type: format, children: [] }
    Transforms.wrapNodes(editor, block)
  }
}

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format)

  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

export const removeLink = (editor, opts = {}) => {
  return Transforms.unwrapNodes(editor, {
    ...opts,
    match: (node) => !Editor.isEditor(node) && Element.isElement(node),
  })
}

export const insertLink = (editor, { url, text }) => {
  const { selection } = editor
  const link = createLinkNode(url, text)
  ReactEditor.focus(editor)
  if (!!selection) {
    const [parentNode, parentPath] = Editor.parent(editor, selection.focus?.path)
    if (parentNode.type === NODE_TYPE.LINK) {
      removeLink(editor)
    }

    if (editor.isVoid(parentNode)) {
      return Transforms.insertNodes(editor, createParagraphNode([link]), {
        at: Path.next(parentPath),
        select: true,
      })
    }

    if (Range.isCollapsed(selection)) {
      return Transforms.insertNodes(editor, link, { select: true })
    }

    Transforms.wrapNodes(editor, link, { split: true })
    Transforms.insertText(editor, text)
    Transforms.collapse(editor, { edge: 'end' })
    return
  }

  Transforms.insertNodes(editor, createParagraphNode([link]))
}

export const getLinkData = (editor) => {
  const { selection } = editor

  if (!!selection) {
    const [parentNode] = Editor.parent(editor, selection?.focus?.path)
    const selectedText = Editor.string(editor, selection)
    const isLink = parentNode.type === NODE_TYPE.LINK
    const hasSelection = Range.isExpanded(selection)

    if (hasSelection && isLink) {
      const { href } = parentNode
      return { text: selectedText, url: href }
    }

    if (isLink) {
      const {
        children: [element],
        href,
      } = parentNode
      return { text: element.text, url: href }
    }
    return { text: selectedText, url: '' }
  }
  return { text: '', url: '' }
}

export const selectNode = (editor) => {
  ReactEditor.focus(editor)
  const { selection } = editor
  const [parentNode] = Editor.parent(editor, selection.focus?.path)

  if (parentNode.type === NODE_TYPE.LINK) {
    const linkNode = ReactEditor.toDOMNode(editor, parentNode)
    window.getSelection().selectAllChildren(linkNode)
  }
}

export const withKeyBindings = (editor) => {
  const { insertBreak } = editor
  editor.insertBreak = () => {
    const [selectedElement, path] = Editor.parent(editor, editor.selection)

    if (selectedElement.type === NODE_TYPE.LINK) {
      const endPoint = Range.end(editor.selection)
      const [selectedLeaf] = Editor.node(editor, endPoint)
      if (selectedLeaf.text.length === endPoint.offset) {
        if (Range.isExpanded(editor.selection)) {
          Transforms.delete(editor)
        }
        Transforms.select(editor, Path.next(path))
      }
    }
    insertBreak()
  }
  return editor
}

export const withLinks = (editor) => {
  const { isInline } = editor
  editor.isInline = (element) => element.type === NODE_TYPE.LINK || isInline(element)
  return editor
}

const ELEMENT_TAGS = {
  A: (el) => ({ type: 'link', href: el.getAttribute('href') }),
  BLOCKQUOTE: () => ({ type: 'block-quote' }),
  H1: () => ({ type: 'heading-one' }),
  H2: () => ({ type: 'heading-two' }),
  H3: () => ({ type: 'heading-three' }),
  H4: () => ({ type: 'heading-four' }),
  H5: () => ({ type: 'heading-five' }),
  H6: () => ({ type: 'heading-six' }),
  IMG: (el) => ({ type: 'image', url: el.getAttribute('src') }),
  LI: () => ({ type: 'list-item' }),
  OL: () => ({ type: 'numbered-list' }),
  P: () => ({ type: 'paragraph' }),
  DIV: () => ({ type: 'div' }),
  // SPAN: () => ({ type: 'span' }),
  PRE: () => ({ type: 'code' }),
  UL: () => ({ type: 'bulleted-list' }),
  BR: () => ({ type: 'break' }),
}

// COMPAT: `B` is omitted here because Google Docs uses `<b>` in weird ways.
const TEXT_TAGS = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
}

export const deserialize = (el) => {
  if (el.nodeType === 3) {
    return el.textContent
  } else if (el.nodeType !== 1) {
    return null
  } else if (el.nodeName === 'BR') {
    // return '\n'
  }

  const { nodeName } = el
  let parent = el
  if (nodeName === 'PRE' && el.childNodes[0] && el.childNodes[0].nodeName === 'CODE') {
    parent = el.childNodes[0]
  }
  let children = Array.from(parent.childNodes).map(deserialize).flat()

  const shortCode = SHORT_CODES_LIST.filter(
    (shortCode) => shortCode === '<' + nodeName.toUpperCase() + '>'
  )
  if (shortCode.length) {
    children.unshift(shortCode)
  }

  if (children.length === 0) {
    children = [{ text: '' }]
  }

  if (el.nodeName === 'BODY') {
    return jsx('fragment', {}, children)
  }

  if (ELEMENT_TAGS[nodeName]) {
    const attrs = ELEMENT_TAGS[nodeName](el)
    return jsx('element', attrs, children)
  }

  if (TEXT_TAGS[nodeName]) {
    const attrs = TEXT_TAGS[nodeName](el)
    return children.map((child) => jsx('text', attrs, child))
  }

  return children
}
const serializeMark = (type, value, text) => {
  switch (type) {
    case MARK_TYPES.BOLD:
      return <strong>{text || value}</strong>

    case MARK_TYPES.ITALIC:
      return <em>{text || value}</em>

    case MARK_TYPES.UNDERLINE:
      return <u>{text || value}</u>

    case MARK_TYPES.CODE:
      return <code>{text || value}</code>

    default:
      return text || value
  }
}

const serializeChildren = (obj) => {
  return obj.children.map((child) => {
    if (Object.keys(child).some((key) => Object.values(MARK_TYPES).includes(key))) {
      let textElement = child.text || ''

      Object.entries(child).forEach(([key, value]) => {
        if (Object.values(MARK_TYPES).includes(key)) {
          textElement = serializeMark(key, value, textElement)
        }
      })
      return { text: ReactDOMServer.renderToStaticMarkup(textElement) }
    }

    if (child.type === ELEMENT_TYPES.LINK) {
      return {
        text: ReactDOMServer.renderToStaticMarkup(
          <a href={child.href}>{child.children.map((linkText) => linkText.text)}</a>
        ),
      }
    }
    return child
  })
}

const serializeList = (children) => {
  return children.map((child) => {
    return (
      <li>
        {serializeChildren(child)
          ?.map((child) => child.text)
          .join('')}
      </li>
    )
  })
}

export const serialize = (obj) => {
  const type = obj.type
  const children = serializeChildren(obj)
  const text = children?.map((child) => child.text).join('')
  switch (type) {
    case ELEMENT_TYPES.PARAGRAPH:
      return <p>{text}</p>
    case ELEMENT_TYPES.BLOCK_QUOTE:
      return <blockquote>{text}</blockquote>
    case ELEMENT_TYPES.BULLETED_LIST:
      return <ul>{serializeList(children)}</ul>
    case ELEMENT_TYPES.HEADING_ONE:
      return <h1>{text}</h1>
    case ELEMENT_TYPES.HEADING_TWO:
      return <h2>{text}</h2>
    case ELEMENT_TYPES.NUMBERED_LIST:
      return <ol>{serializeList(children)}</ol>
    case ELEMENT_TYPES.DIV:
      return <div>{text}</div>
    case ELEMENT_TYPES.SPAN:
      return <span>{text}</span>
    case ELEMENT_TYPES.BREAK:
      return text ? <div>{text}</div> : <br />
    case ELEMENT_TYPES.CODE:
      return (
        <pre>
          <code>{text}</code>
        </pre>
      )
    default:
      return <div>text</div>
  }
}

export const isHTML = (str) => {
  return str?.match(/&lt;/gi) || str?.match(/&gt;/gi) || str?.match(/&amp;/gi) || str?.match(/<\//)
}

export const parseHTMLFromStringMarkTag = (valueObject) => {
  const parsed = new DOMParser().parseFromString(valueObject.text, 'text/html')
  const deserializedValue = deserialize(parsed.body)
  return deserializedValue.flatMap((val) => {
    if (isHTML(val.text)) {
      return parseHTMLFromStringMarkTag({
        ...valueObject,
        ...val,
        text: val.text,
      })
    }
    return { ...valueObject, ...val, text: val.text }
  })
}
const parseChildren = (val) => {
  return {
    ...val,
    children: val?.children?.flatMap((child) => {
      if (child.children) {
        return parseChildren(child)
      }
      if (isHTML(child.text)) {
        return parseHTMLFromStringMarkTag(child)
      }
      return { text: child.text }
    }),
  }
}
export const parseHTMLFromString = (HTMLstring) => {
  if (HTMLstring) {
    let parsed = new DOMParser().parseFromString(HTMLstring, 'text/html')
    if (parsed.body.innerText && isHTML(parsed.body.innerText) && !parsed.body.children.length) {
      parsed = new DOMParser().parseFromString(parsed.body.innerText, 'text/html')
    }
    const deserializedValue = deserialize(parsed.body)

    const deserializedChildren = deserializedValue.flatMap((val) => {
      return parseChildren(val)
    })
    if (deserializedChildren.length > 0) {
      return deserializedChildren
    }
    return parseHTMLFromString(deserializedValue[0].text)
  }
  return []
}

export const parseSubject = (HTMLstring) => {
  if (isHTML(HTMLstring)) {
    const parsed = new DOMParser().parseFromString(HTMLstring, 'text/html')
    return parsed.body.innerText
  }
  return HTMLstring
}

export const renderOnDivWithShortcodes = (html, options = {}) => {
  const {
    preventPlaceholderLinks = false,
    preserveContentEditable = false,
    sanitize = false,
  } = options

  if (!html) return ''

  const txt = document.createElement('textarea')
  txt.innerHTML = html
  let result = txt.value

  // Replaces shortcodes with temporary placeholders (so they pass through sanitization)
  const placeholders = {}
  let counter = 0

  SHORT_CODES_LIST.forEach((code) => {
    const placeholder = `__SHORTCODE_${counter}__`
    placeholders[placeholder] = code

    const encodedCode = code.replace(/</g, '&lt;').replace(/>/g, '&gt;')

    const escapedCode = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const escapedEncodedCode = encodedCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    const regexUnencoded = new RegExp(escapedCode, 'gi')
    const regexEncoded = new RegExp(escapedEncodedCode, 'gi')

    result = result.replace(regexEncoded, placeholder)
    result = result.replace(regexUnencoded, placeholder)

    counter++
  })

  if (sanitize) {
    result = DOMPurify.sanitize(
      result,
      preserveContentEditable ? { ADD_ATTR: ['contenteditable'] } : {}
    )
  }

  Object.entries(placeholders).forEach(([placeholder, code]) => {
    const encodedShortcode = code.replace(/</g, '&lt;').replace(/>/g, '&gt;')

    result = result.replace(new RegExp(placeholder, 'g'), encodedShortcode)
  })

  // block all links in the html if preventPlaceholderLinks is true
  if (preventPlaceholderLinks) {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = result

    // Find all links in the HTML
    const links = tempDiv.querySelectorAll('a')
    links.forEach((link) => {
      const href = link.getAttribute('href')
      // Remove the condition that checks for shortcodes to disable ALL links

      // Instead of javascript:void(0), use # and prevent default behavior
      link.setAttribute('href', '#')
      // Add a class for styling if needed
      link.classList.add('placeholder-link')
      // Store the original href as a data attribute if needed for reference
      link.setAttribute('data-original-href', href)
      // Add onClick to prevent default behavior
      link.setAttribute('onclick', 'event.preventDefault()')
      // Add a title attribute to indicate this is a placeholder
      link.setAttribute('title', 'Link disabled in preview mode')
    })

    result = tempDiv.innerHTML
  }

  return result
}
